import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import {
  IBrand,
  IModel,
  IEngine,
  ITireSize,
  IDeviceProvider,
  IDevice,
} from 'interfaces/vehicle-config-interfaces';

interface IVehicleConfigurationState {
  brands: IBrand[];
  selectedBrand: IBrand | null;
  engines: IEngine[];
  selectedEngine: IEngine | null;
  tireSizes: ITireSize[];
  selectedTireSize: ITireSize | null;
  models: IModel[];
  selectedModel: IModel | null;
  deviceProviders: IDeviceProvider[];
  selectedDeviceProvider: IDeviceProvider | null;
  devices: IDevice[];
  selectedDevice: IDevice | null;
  totalBrandCount: number;
  totalEngineCount: number;
  totalTireSize: number;
  totalModelSize: number;
  totalProviderSize: number;
}
interface IBrandResponse {
  brands: IBrand[];
  record_count: number;
}
interface IEngineResponse {
  engine: IEngine[];
  record_count: number;
}

interface ITireSizeResponse {
  tires: ITireSize[];
  record_count: number;
}
interface IModelResponse {
  model: IModel[];
  record_count: number;
}
interface IDeviceProviderResponse {
  providers: IDeviceProvider[];
  record_count: number;
}
const initialState: IVehicleConfigurationState = {
  brands: [],
  selectedBrand: null,
  engines: [],
  selectedEngine: null,
  tireSizes: [],
  selectedTireSize: null,
  models: [],
  selectedModel: null,
  deviceProviders: [],
  selectedDeviceProvider: null,
  devices: [],
  selectedDevice: null,
  totalBrandCount: 0,
  totalEngineCount: 0,
  totalTireSize: 0,
  totalModelSize: 0,
  totalProviderSize: 0,
};
const vehicleConfigurationSlice = createSlice({
  name: 'VehicleConfig',
  initialState: initialState,
  reducers: {
    setBrands: (state, { payload }: { payload: IBrandResponse }) => {
      state.brands = [...payload.brands];
      state.totalBrandCount = payload.record_count;
    },
    setSelectedBrand: (state, { payload }: { payload: IBrand | null }) => {
      state.selectedBrand = payload;
    },
    setEngines: (state, { payload }: { payload: IEngineResponse }) => {
      state.engines = [...payload.engine];
      state.totalEngineCount = payload.record_count;
    },
    setSelectedEngine: (state, { payload }: { payload: IEngine | null }) => {
      state.selectedEngine = payload;
    },
    setTireSizes: (state, { payload }: { payload: ITireSizeResponse }) => {
      state.tireSizes = [...payload.tires];
      state.totalTireSize = payload.record_count;
    },
    setSelectedTireSize: (state, { payload }: { payload: ITireSize | null }) => {
      state.selectedTireSize = payload;
    },
    setModels: (state, { payload }: { payload: IModelResponse }) => {
      state.models = [...payload.model];
      state.totalModelSize = payload.record_count;
    },
    setSelectedModel: (state, { payload }: { payload: IModel | null }) => {
      state.selectedModel = payload;
    },
    setDeviceProviders: (state, { payload }: { payload: IDeviceProviderResponse }) => {
      state.deviceProviders = [...payload.providers];
      state.totalProviderSize = payload.record_count;
    },
    setSelectedDeviceProvider: (state, { payload }: { payload: IDeviceProvider | null }) => {
      state.selectedDeviceProvider = payload;
    },
    setDevices: (state, { payload }: { payload: IDevice[] | [] }) => {
      state.devices = payload;
    },
    setSelectedDevice: (state, { payload }: { payload: IDevice | null }) => {
      state.selectedDevice = payload;
    },
  },
});

export const createBrand = (payload: { name: string; image: string }) => async () => {
  try {
    const { data: responseData } = await api.post('/admin/brand', { ...payload });
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createEngine = (payload: { name: string }) => async () => {
  try {
    const { data: responseData } = await api.post('/admin/engine', { ...payload });
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createTireSize = (payload: { name: string }) => async () => {
  try {
    const { data: responseData } = await api.post('/admin/tire/sizes', { ...payload });
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createModel =
  (payload: { name: string; image: string; brand_id: string }) => async () => {
    try {
      const { data: responseData } = await api.post('/admin/model', { ...payload });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateBrand =
  (payload: { request: { brand_id: string; name: string; image: string }; brand_id: string }) =>
  async () => {
    try {
      const { request, brand_id } = payload;
      const { data: responseData } = await api.post(`/admin/brand/${brand_id}`, { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateEngine =
  (payload: { request: { engine_id: string; name: string }; engine_id: string }) => async () => {
    try {
      const { request, engine_id } = payload;
      const { data: responseData } = await api.post(`/admin/engine/${engine_id}`, { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateTireSize =
  (payload: { request: { tire_size_id: string; name: string }; tire_size_id: string }) =>
  async () => {
    try {
      const { request, tire_size_id } = payload;
      const { data: responseData } = await api.post(`/admin/tire/sizes/${tire_size_id}`, {
        ...request,
      });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateModel =
  (payload: {
    request: { model_id: string; name: string; image: string; brand_id: string };
    model_id: string;
  }) =>
  async () => {
    try {
      const { request, model_id } = payload;
      const { data: responseData } = await api.post(`/admin/model/${model_id}`, { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const deleteBrand = (payload: { brand_id: string }) => async () => {
  try {
    const { brand_id } = payload;
    const { data: responseData } = await api.delete(`/admin/brand/${brand_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const deleteEngine = (payload: { engine_id: string }) => async () => {
  try {
    const { engine_id } = payload;
    const { data: responseData } = await api.delete(`/admin/engine/${engine_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const deleteTireSize = (payload: { tire_size_id: string }) => async () => {
  try {
    const { tire_size_id } = payload;
    const { data: responseData } = await api.delete(`/admin/tire/size/${tire_size_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const deleteModel = (payload: { model_id: string }) => async () => {
  try {
    const { model_id } = payload;
    const { data: responseData } = await api.delete(`/admin/model/${model_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getBrandList =
  (payload?: { searchText?: string; offset?: number; limit?: number }) =>
  async (dispatch: Dispatch) => {
    try {
      // const {searchText, offset, limit} = payload

      const { data: responseData } = await api.get('/admin/brands', { params: { ...payload } });
      dispatch(setBrands(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getEngineList =
  (payload: { searchText?: string; offset?: number; limit?: number }) =>
  async (dispatch: Dispatch) => {
    try {
      const { searchText, offset, limit } = payload;

      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchText ? `&searchText=${encodeURIComponent(searchText)}` : ''
      }`;
      const { data: responseData } = await api.get(`/admin/engines${queryString}`);
      dispatch(setEngines(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getTireSizeList =
  (payload: { searchText?: string; offset?: number; limit?: number }) =>
  async (dispatch: Dispatch) => {
    try {
      const { searchText, offset, limit } = payload;

      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchText ? `&searchText=${encodeURIComponent(searchText)}` : ''
      }`;
      const { data: responseData } = await api.get(`/admin/tire/sizes${queryString}`);
      dispatch(setTireSizes(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getModelsList = (payload: { searchText?: string }) => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/model', { params: { ...payload } });
    dispatch(setModels(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getDeviceProvidersList =
  (payload: { searchText?: string; offset?: number; limit?: number }) =>
  async (dispatch: Dispatch) => {
    try {
      const { searchText, offset, limit } = payload;

      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchText ? `&searchText=${encodeURIComponent(searchText)}` : ''
      }`;
      const { data: responseData } = await api.get(`/admin/providers${queryString}`);
      dispatch(setDeviceProviders(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getDevicesList = (payload: { searchText?: string }) => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/devices', {
      params: { ...payload },
    });
    dispatch(setDevices(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createDeviceProvider = (payload: { name: string }) => async () => {
  try {
    const { data: responseData } = await api.post('/admin/provider', { ...payload });
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const updateDeviceProvider =
  (payload: { request: { name: string }; provider_id: string }) => async () => {
    try {
      const { request, provider_id } = payload;
      const { data: responseData } = await api.post(`/admin/provider/${provider_id}`, {
        ...request,
      });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const deleteDeviceProvider = (payload: { provider_id: string }) => async () => {
  try {
    const { provider_id } = payload;
    const { data: responseData } = await api.delete(`/admin/provider/${provider_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const deleteDevice = (payload: { device_id: string }) => async () => {
  try {
    const { device_id } = payload;
    const { data: responseData } = await api.delete(`/admin/device/${device_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createDevice = (payload: { imeie: string; provider_id: string }) => async () => {
  try {
    const { data: responseData } = await api.post('/admin/device', { ...payload });
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const updateDevice =
  (payload: { request: { imeie: string; provider_id: string }; device_id: string }) => async () => {
    try {
      const { request, device_id } = payload;
      const { data: responseData } = await api.post(`/admin/device/${device_id}`, {
        ...request,
      });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const {
  setBrands,
  setSelectedBrand,
  setEngines,
  setSelectedEngine,
  setSelectedTireSize,
  setTireSizes,
  setModels,
  setSelectedModel,
  setDeviceProviders,
  setSelectedDeviceProvider,
  setDevices,
  setSelectedDevice,
} = vehicleConfigurationSlice.actions;
export default vehicleConfigurationSlice.reducer;
